import React, { useEffect, useMemo, useState } from 'react';

import { Home } from '@mui/icons-material';
import { Spinner } from 'react-bootstrap';
import { MdEdit } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { usePagination, useTable } from 'react-table';

import { listAllContractActivities, listAllDmsDrivers } from './operations';
import { clientLabel, downloadCsv, driverLabel } from './utils';
import {
  Breadcrumbs,
  Button,
  ExportCsvButton,
  Footer,
  FormContainer,
  FormSelectField,
  SubHeader,
  TablePagination,
} from '../../components';
import { formatIsoToDate } from '../../utils';

import './styles.scoped.css';

const SearchDrivers = () => {
  const navigate = useNavigate();
  const [driverOptions, setDriverOptions] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    Promise.all([listAllDmsDrivers(), listAllContractActivities()])
      .then(([drivers, activityResponse]) => {
        setDriverOptions(
          drivers
            .sort((a, b) => (a.lastName || '').localeCompare(b.lastName || ''))
            .map(driver => ({ label: driverLabel(driver), value: driver.driverId })),
        );
        setActivities(activityResponse);
      })
      .catch(e => setLoadError(e.message))
      .finally(() => setLoading(false));
  }, []);

  // Contract Activity picklist cascades to the selected driver so the two
  // criteria can never contradict each other.
  const activityOptions = useMemo(
    () =>
      activities
        .filter(activity => !selectedDriver || activity.driverId === selectedDriver.value)
        .sort((a, b) => a.id.localeCompare(b.id))
        .map(({ id }) => ({ label: id, value: id })),
    [activities, selectedDriver],
  );

  const handleDriverChange = driver => {
    setSelectedDriver(driver);
    // Drop a contract activity that doesn't belong to the newly picked driver
    if (driver && selectedActivity) {
      const belongs = activities.some(a => a.id === selectedActivity.value && a.driverId === driver.value);
      if (!belongs) setSelectedActivity(null);
    }
  };

  // Sort by Contract Activity Start Date, newest to oldest (UC 7.1 results)
  const results = useMemo(
    () =>
      activities
        .filter(activity => !selectedDriver || activity.driverId === selectedDriver.value)
        .filter(activity => !selectedActivity || activity.id === selectedActivity.value)
        .sort((a, b) => (b.contractStartDate || '').localeCompare(a.contractStartDate || '')),
    [activities, selectedDriver, selectedActivity],
  );

  const columns = useMemo(
    () => [
      {
        Header: 'Driver Name & Number',
        accessor: row => [row.driverName, row.driverId].filter(Boolean).join(' - '),
        id: 'driver',
      },
      {
        Header: 'Client Name & Number',
        accessor: row => clientLabel(row.clientName, row.clientId),
        id: 'client',
      },
      { Header: 'LOB', accessor: 'lob' },
      { Header: 'Contract Activity ID', accessor: 'id' },
      { Header: 'Contract Start Date', accessor: row => formatIsoToDate(row.contractStartDate), id: 'start' },
      { Header: 'Contract End Date', accessor: row => formatIsoToDate(row.contractEndDate), id: 'end' },
      { Header: 'Start Ben. Sent Date', accessor: row => formatIsoToDate(row.startBenSentDate), id: 'benStart' },
      { Header: 'Term Ben. Sent Date', accessor: row => formatIsoToDate(row.termBenSentDate), id: 'benTerm' },
      {
        Header: '',
        id: 'edit',
        Cell: ({ row }) => (
          <button
            type="button"
            className="icon-button"
            aria-label={`Edit contract activity ${row.original.id}`}
            onClick={() => navigate(`/dms-driver-log/contract-activity/${row.original.id}`)}>
            <MdEdit className="edit-icon" />
          </button>
        ),
      },
    ],
    [navigate],
  );

  const tableInstance = useTable({ columns, data: results, initialState: { pageSize: 25 } }, usePagination);
  const { getTableProps, getTableBodyProps, headerGroups, page, prepareRow } = tableInstance;

  const handleExport = () =>
    downloadCsv(
      'dms-driver-search-results.csv',
      [
        'Driver Name & Number',
        'Client Name & Number',
        'LOB',
        'Contract Activity ID',
        'Contract Start Date',
        'Contract End Date',
        'Start Ben. Sent Date',
        'Term Ben. Sent Date',
      ],
      results.map(activity => [
        [activity.driverName, activity.driverId].filter(Boolean).join(' - '),
        clientLabel(activity.clientName, activity.clientId),
        activity.lob,
        activity.id,
        formatIsoToDate(activity.contractStartDate),
        formatIsoToDate(activity.contractEndDate),
        formatIsoToDate(activity.startBenSentDate),
        formatIsoToDate(activity.termBenSentDate),
      ]),
    );

  return (
    <>
      <SubHeader
        text="DMS Driver Log"
        buttons={[{ icon: <Home sx={{ color: '#fff', fontSize: 30 }} />, onPress: () => navigate('/dashboard') }]}
      />
      <Breadcrumbs
        leftTitle="DMS Driver Log"
        middleTitle="Search Drivers"
        midItemAction={() => setShowResults(false)}
        rightTitle={showResults ? 'Search Results' : undefined}
        route="/dms-driver-log"
      />
      <div className="wrap">
        {loadError && <div className="field-error">{loadError}</div>}
        {!showResults ? (
          <>
            <div className="header-actions">
              <Button variant="primary" text="SEARCH" onPress={() => setShowResults(true)} />
              <Button
                variant="neutral"
                text="RESET FILTERS"
                onPress={() => {
                  setSelectedDriver(null);
                  setSelectedActivity(null);
                }}
              />
            </div>
            <div className="search-card">
              <FormContainer title="Search">
                <FormSelectField
                  label="Driver Name & Driver Number"
                  options={driverOptions}
                  loading={loading}
                  isClearable
                  value={selectedDriver}
                  onChange={handleDriverChange}
                />
                <FormSelectField
                  label="Contract Activity Number"
                  options={activityOptions}
                  loading={loading}
                  isClearable
                  value={selectedActivity}
                  onChange={setSelectedActivity}
                />
              </FormContainer>
            </div>
          </>
        ) : (
          <>
            <div className="export-row">
              <ExportCsvButton onClick={handleExport} />
            </div>
            <div className="list-controls">
              <div />
              <TablePagination tableInstance={tableInstance} />
            </div>
            {loading ? (
              <Spinner animation="border" style={{ margin: '40px auto', color: 'var(--primary)' }} />
            ) : results.length === 0 ? (
              <div className="no-results">No results found for the selected criteria.</div>
            ) : (
              <table className="driver-table" {...getTableProps()}>
                <thead>
                  {headerGroups.map(headerGroup => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map(column => (
                        <th {...column.getHeaderProps()}>{column.render('Header')}</th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {page.map(row => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()}>
                        {row.cells.map(cell => (
                          <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
      <Footer />
    </>
  );
};

export default SearchDrivers;
